/**
 * One dialog shell, with the three things the prototype does not do: focus moves into the dialog
 * on open and back to the trigger on close, focus is trapped while it is open, and the
 * background is made `inert` with its scroll locked.
 *
 * One backdrop handler, owned here. The prototype registers two, so a backdrop click renders
 * twice (`contradictions.md` #19).
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '~/hooks/useEscapeKey';
import { Button } from './Button';
import styles from './Modal.module.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  title: string;
  subtitle?: string;
  role?: 'dialog' | 'alertdialog';
  maxWidth?: number;
  onDismiss: () => void;
  footer?: ReactNode;
  children: ReactNode;
}

export function Modal({
  title,
  subtitle,
  role = 'dialog',
  maxWidth,
  onDismiss,
  footer,
  children,
}: ModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const openedFrom = useRef<HTMLElement | null>(null);

  useEscapeKey(true, onDismiss);

  useEffect(() => {
    openedFrom.current = document.activeElement as HTMLElement | null;
    const root = document.getElementById('root');
    const previousOverflow = document.body.style.overflow;

    root?.setAttribute('inert', '');
    document.body.style.overflow = 'hidden';

    const first = cardRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? cardRef.current)?.focus();

    return () => {
      root?.removeAttribute('inert');
      document.body.style.overflow = previousOverflow;
      openedFrom.current?.focus();
    };
  }, []);

  const trap = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const card = cardRef.current;
    if (!card) return;
    // The selector already excludes disabled controls and tabindex="-1"; what is left to exclude
    // is anything hidden from the accessibility tree.
    const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (element) =>
        !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const titleId = `modal-title-${title.replace(/\W+/g, '-').toLowerCase()}`;

  return createPortal(
    <div
      className={styles.wrap}
      role="presentation"
      onMouseDown={(event) => {
        // One backdrop handler, owned here. Escape is the keyboard equivalent and the dialog
        // traps focus, so the backdrop itself is never a tab stop.
        if (event.target === event.currentTarget) onDismiss();
      }}
    >
      <div
        ref={cardRef}
        className={styles.modal}
        style={maxWidth === undefined ? undefined : { maxWidth }}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={trap}
      >
        <div className={styles.head}>
          <h2 id={titleId}>{title}</h2>
          {subtitle ? <p className={styles.sub}>{subtitle}</p> : null}
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.foot}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ModalSpacer() {
  return <span className={styles.spacer} />;
}

export interface ConfirmDialogProps {
  title: string;
  subtitle?: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** The confirm control is inert — the dialog is asking for something it has not been given. */
  inactive?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  subtitle,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  inactive = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Modal
      title={title}
      {...(subtitle ? { subtitle } : {})}
      role="alertdialog"
      maxWidth={480}
      onDismiss={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{cancelLabel}</Button>
          <ModalSpacer />
          <Button
            variant={destructive ? 'dangerSolid' : 'primary'}
            onClick={onConfirm}
            inactive={inactive}
            pending={pending}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Modal>
  );
}
